import { Grid, LinearProgress, Paper, Typography } from '@material-ui/core';
import { cloneDeep, isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { getDateGroupNumber, getValidDateGroup } from '~/src/dash/api/console-api';
import {
  IIASFirstAndThirdPartyRequestBody,
  queryFirstAndThirdPartyIasData,
} from '~/src/dash/api/ias-wrapper-api/ias-wrapper-api';
import { IFocusedInput } from '~/src/dash/components/calendar/calendar-wrapper';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IStore } from '~/src/dash/reducers/store';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { IBaseState } from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { DateGroupDays, formatDateStartDay, getStartDate } from '~/src/dash/utils/date';
import { decorateV2 } from '~/src/dash/utils/decorator';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { routeName } from '~/src/dash/utils/routes';
import {
  combineDeviceTypeFilters,
  filterGroupBy,
  generateCalendarProps,
} from '~/src/dash/views/common/ias-data-consts';
import { IASMeasurementTrackEvent } from '~/src/dash/views/measurement/common/consts';
import {
  IMeasurementBreadCrumb,
  IMeasurementQueryParamsNoComp,
} from '~/src/dash/views/measurement/common/types';
import {
  IMeasurementDynamicFilter,
  IMeasurementFilter,
  IMeasurementTableGroupBy,
  measurementDrillConfig,
} from '~/src/dash/views/measurement/config';
import { IMeasurementBreadCrumbs } from '~/src/dash/views/measurement/MeasurementDrill/MeasurementBreadCrumbs';
import { MeasurementChart } from '~/src/dash/views/measurement/MeasurementDrill/MeasurementChart';
import { MeasurementFilters } from '~/src/dash/views/measurement/MeasurementDrill/MeasurementFilters';
import { MeasurementTable } from '~/src/dash/views/measurement/MeasurementDrill/MeasurementTable';
import {
  generateSnackbarState,
  ISnackbarState,
  SnackbarView,
} from '~/src/dash/views/snackbar-view';

const cfg = measurementDrillConfig;

const generateDynamicFiltersSelectList = (
  data: Array<Record<string, any>>,
  showID: boolean | undefined = false,
): IMenuList => {
  const list = {} as IMenuList;
  data.forEach((row: Record<string, any>) => {
    const idString = typeof row.ID === 'number' ? row.ID.toString() : row.ID;
    list[idString] = {
      ID: idString,
      title: showID ? `${row.Name} [${idString}]` : row.Name,
      selected: false,
    } as IListItem;
  });
  return list;
};

const extractDynamicFilterValues = (
  filterKey: string,
  filters: Record<string, IMeasurementFilter>,
): string[] => {
  const values = [] as string[];
  if (filters[filterKey]) {
    const list = Object.values(filters[filterKey].list);
    list.forEach((item) => {
      if (item.selected) {
        values.push(item.ID.toString());
      }
    });
  }
  return values;
};

const generateGenericDate = (timezone: string) => {
  const date = getStartDate(timezone, 7, 'day').startOf('day');
  return formatDateStartDay(date);
};

const generateGenericQueryParams = (timezone: string, session: ISessionState) => {
  const date = generateGenericDate(timezone);
  const publisherID = session.publisher.ID;
  const anIDs = [+session.publisher.IASAnID];
  return {
    startDate: date,
    endDate: date,
    timezone,
    groupingNumber: 1,
    deviceTypes: [],
    teamID: cfg.teamID,
    anIDs,
    publisherID,
  } as Partial<IMeasurementQueryParamsNoComp>;
};

const extractDefaultGroupBy = (groupBys: IMeasurementTableGroupBy[]) => {
  const result = groupBys.filter((item) => item.isDefault);
  return result.length > 0 ? result[0] : groupBys[0];
};

const filterGroupBysByTree = (tree: string, groupBys: IMeasurementTableGroupBy[]) => {
  return groupBys.filter((groupBy) => groupBy.tree === tree);
};

const getTreeFromGroupByDataKey = (key: string, groupBys: IMeasurementTableGroupBy[]) => {
  const result = groupBys.find(({ dataKey }) => dataKey === key);
  return result ? result.tree : '';
};

interface IMeasurementDrillProps extends IStore, IBaseState {}

export const _MeasurementDrill = ({ classes, session, user, location }: IMeasurementDrillProps) => {
  const isMounted = useRef(false);
  // track handlers
  const [handleType, setHandleType] = useState('');
  // filters states
  const [filtersLoadState, setFiltersLoadState] = useState(LoadingStateLoading);
  const [filters, setFilters] = useState<Record<string, IMeasurementFilter>>(cfg.filters);
  const [dynamicFiltersData, setDynamicFiltersData] = useState(
    [] as Array<Array<Record<string, any>>>,
  );
  const [rangeSelectedStr, setRangeSelectedStr] = useState('Last 7 Days');
  const [calendar, setCalendar] = useState(generateCalendarProps(cfg.timezone));
  const [calFocusedInput, setCalFocusedInput] = useState<IFocusedInput>(null);
  // const [compareCalendar, setCompareCalendar] = useState(generateCompareCalendar(cfg.timezone));
  // const [compFocusedInput, setCompFocusedInput] = useState<IFocusedInput>(null);
  const [densityRangeAgg, setDensityRangeAgg] = useState(DateGroupDays);
  // const [isComparedToEnabled, setIsComparedToEnabled] = useState(true);
  // breadcrumb state
  const [crumbs, setCrumbs] = useState<IMeasurementBreadCrumb[]>([]);
  const [isCrumbUsed, setIsCrumbUsed] = useState(false);
  // chart state -- calendar/compare calendar data
  const [chartDataKey, setChartDataKey] = useState(cfg.table.columns[0].dataKey);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [currentNameSearch, setCurrentNameSearch] = useState('');
  const [currentIDSearch, setCurrentIDSearch] = useState('');
  const [currentTableOrderDirection, setCurrentTableOrderDirection] = useState('desc');
  // additional api params
  const [queryParams, setQueryParams] = useState({} as IMeasurementQueryParamsNoComp);
  const [currentGroupBy, setCurrentGroupBy] = useState(extractDefaultGroupBy(cfg.table.groupBys));
  // table state
  const [filterConfig, setFilterConfig] = useState({} as Record<string, any>);
  // snackbar state
  const [snackbar, setSnackbar] = useState<ISnackbarState>(generateSnackbarState());

  const genericQueryParams = generateGenericQueryParams(cfg.timezone, session);
  const { pathname } = location;

  // track the initial page load
  useEffect(() => {
    IASMeasurementTrackEvent(user.user, {
      eventTitle: 'IAS Measurement Drill Dashboard - View',
    });
  }, []);

  // whenever the calendar changes, we reload the filter pills to match the date window
  useEffect(() => {
    if (
      handleType !== 'handleCalendarDateChange' &&
      handleType !== 'handleCalendarFocusedInputChange' &&
      calendar.startDate &&
      calendar.endDate
    ) {
      getDynamicFilterItems(cfg.dynamicFilters, calendar.startDate, calendar.endDate);
    }
  }, [
    calendar,
    // compareCalendar,
  ]);

  // once the dynamic filters are downloaded, we add them to the filter state
  useEffect(() => {
    if (isMounted.current && dynamicFiltersData.length > 0) {
      const clonedFilters = cloneDeep(cfg.filters);
      cfg.dynamicFilters.map((filter, i) => {
        const { menuTitle, showID } = filter;
        clonedFilters[menuTitle] = {
          menuTitle,
          list: generateDynamicFiltersSelectList(dynamicFiltersData[i], showID),
        };
      });
      setFilters(clonedFilters);
    }
  }, [dynamicFiltersData]);

  // handles calendar changes
  // useEffect(() => {
  //   if (isMounted.current &&
  //     handleType !== 'handleDensityRangeChangeAgg' &&
  //     handleType !== 'handleCalendarFocusedInputChange'
  //   ) {
  //     const updatedCompStartDate = findCompareStartDate({
  //       timezone: cfg.timezone,
  //       startDate: calendar.displayStartDate,
  //       endDate: calendar.endDate,
  //       rangeSelected: rangeSelectedStr,
  //     });
  //     const updatedCompEndDate = findCompareEndDate({
  //       timezone: cfg.timezone,
  //       startDate: calendar.displayStartDate,
  //       endDate: calendar.displayEndDate,
  //       rangeSelected: rangeSelectedStr,
  //     });
  //     const clonedCompareCalendar = cloneDeep(compareCalendar);
  //     clonedCompareCalendar.startDate = updatedCompStartDate;
  //     clonedCompareCalendar.endDate = updatedCompEndDate;
  //     clonedCompareCalendar.displayStartDate = updatedCompStartDate;
  //     clonedCompareCalendar.displayEndDate = updatedCompEndDate;
  //     setCompareCalendar(clonedCompareCalendar);
  //   } else {
  //     isMounted.current = true;
  //   }
  // }, [calendar, densityRangeAgg]);

  // whenever any of the following dependencies change, but not via the following handlers, we update the query params
  useEffect(() => {
    if (isMounted.current && handleType !== 'handleFilterOnChange') {
      updateQueryParams({});
    } else {
      isMounted.current = true;
    }
  }, [currentGroupBy, filters, filterConfig]);

  // Notes: This prevents an unnecessary api call when disabling comparison data
  // useEffect(() => {
  //   if (isMounted.current &&
  //     handleType === 'handleCompareEnabled' &&
  //     isComparedToEnabled
  //   ) {
  // updateQueryParams({});
  //   }
  // }, [isComparedToEnabled]);

  // use api to get info for dynamic filter pills
  const getDynamicFilterItems = (
    dynamicFilters: IMeasurementDynamicFilter[],
    startDate: moment.Moment,
    endDate: moment.Moment,
  ) => {
    const queries: any[] = [];
    const start_date = formatDateStartDay(startDate);
    const end_date = endDate.format('YYYY-MM-DDTHH:mm:ss');
    setFiltersLoadState(LoadingStateLoading);
    dynamicFilters.map((filter) => {
      const body = {
        GraphID: filter.graph,
        StartDate: start_date,
        EndDate: end_date,
        Timezone: genericQueryParams.timezone,
        GroupingNumber: genericQueryParams.groupingNumber,
        DeviceTypes: genericQueryParams.deviceTypes,
        TeamID: genericQueryParams.teamID,
        AnIDs: genericQueryParams.anIDs,
      } as unknown as IIASFirstAndThirdPartyRequestBody;
      queries.push(
        queryFirstAndThirdPartyIasData(body, { publisher_id: genericQueryParams.publisherID }),
      );
    });

    Promise.all(queries)
      .then((data: any) => {
        let queryFailed = false;
        // if the call is successful but there is no data
        if (!data[0][0].rows) {
          queryFailed = true;
          setFiltersLoadState(LoadingStateNoData);
          return;
        }
        const data1: any[] = [];
        data.forEach((item: any) => {
          data1.push(item[0].rows);
        });
        // check the data to make sure it's there, otherwise show an error msg
        if (data1.length === 0) {
          queryFailed = true;
          setFiltersLoadState(LoadingStateNoData);
        }
        if (queryFailed) {
          return;
        }
        // const list = generateDynamicFiltersSelectList(data1, showID)
        unstable_batchedUpdates(() => {
          setDynamicFiltersData(data1 ? data1 : []);
          setFiltersLoadState(LoadingStateHasData);
        });
      })
      .catch(() => {
        setFiltersLoadState(LoadingStateNoData);
      });
  };

  interface IUpdateQueryParams {
    startDate?: moment.Moment | null;
    endDate?: moment.Moment | null;
    // comparedStartDate?: moment.Moment | null;
    // comparedEndDate?: moment.Moment | null;
  }

  // updates the params needed for the api call on subcomponents
  const updateQueryParams = ({
    // comparedEndDate = null,
    // comparedStartDate = null,
    startDate = null,
    endDate = null,
  }: IUpdateQueryParams): void => {
    // query 1 (normal calendar)
    const start_date = formatDateStartDay(startDate || calendar.startDate);
    const end_date = (endDate || calendar.endDate).format('YYYY-MM-DDTHH:mm:ss');
    // query 2 (compare calendar)
    // const comp_start_date = formatDateStartDay(comparedStartDate || compareCalendar.startDate);
    // const comp_end_date = (comparedEndDate || compareCalendar.endDate).format('YYYY-MM-DDTHH:mm:ss');
    // const comp_query_duration_days = compareCalendar.endDate.diff(compareCalendar.startDate, 'days') + 1;
    // query params
    const grouping = densityRangeAgg;
    // get devicetypes
    const deviceTypeFilters = {
      Formats: filters.Formats,
      'Device Types': filters['Device Types'],
    };
    const combinedDeviceTypes = Object.keys(filters).includes('Formats')
      ? combineDeviceTypeFilters(deviceTypeFilters)
      : [];
    const groupingNumber = getDateGroupNumber(grouping);
    const teamID = cfg.teamID;
    const publisherID = session.publisher.ID;
    const anIDs = [+session.publisher.IASAnID];
    const advertiserIDs = extractDynamicFilterValues('Advertisers', filters);
    const orderIDs = extractDynamicFilterValues('Orders', filters);
    const lineItemIDs = extractDynamicFilterValues('Line Items', filters);
    const creativeIDs = extractDynamicFilterValues('Creatives', filters);
    const adUnitIDs = extractDynamicFilterValues('Ad Units', filters);

    // save new request params for other components
    setQueryParams({
      startDate: start_date,
      endDate: end_date,
      // TODO: change comp dates when this feature is implemented
      // compStartDate: comp_start_date,
      // compEndDate: comp_end_date,
      timezone: cfg.timezone,
      groupingNumber,
      deviceTypes: combinedDeviceTypes,
      teamID,
      anIDs,
      publisherID,
      filters: filterConfig,
      groupByKey: currentGroupBy.paramKey,
      advertiserIDs,
      orderIDs,
      lineItemIDs,
      creativeIDs,
      adUnitIDs,
    });
  };

  // calendar functions
  // @ts-ignore REMOVE THIS IGNORE ONCE COMPARE CALENDAR IS NEEDED
  const handleChangeAggRangeSelected = (
    rangeSelected: string,
    isCompareCalendar: boolean,
    applied?: boolean,
  ): void => {
    setHandleType('handleChangeAggRangeSelected');
    // if (isCompareCalendar) {
    //   const clonedCompareCalendar = cloneDeep(compareCalendar);
    //   if (applied) {
    //     clonedCompareCalendar.rangeSelected = rangeSelected;
    //     clonedCompareCalendar.rangeDisplaySelected = rangeSelected;
    //   } else {
    //     clonedCompareCalendar.rangeDisplaySelected = rangeSelected;
    //   }
    //   setCompareCalendar(clonedCompareCalendar);
    // } else {
    const clonedCalendar = cloneDeep(calendar);
    if (applied) {
      clonedCalendar.rangeSelected = rangeSelected;
      clonedCalendar.rangeDisplaySelected = rangeSelected;
    } else {
      clonedCalendar.rangeDisplaySelected = rangeSelected;
    }
    setRangeSelectedStr(rangeSelected);
    setCalendar(clonedCalendar);
    // }
  };

  // this causes a console error due to a bad setState within the nested component
  // no fix needed for now
  // @ts-ignore REMOVE THIS IGNORE ONCE COMPARE CALENDAR IS NEEDED
  const handleCalendarDateChange = () // isCompareCalendar: boolean,
  : ((start: moment.Moment, end: moment.Moment, isCustom: boolean) => void) => {
    setHandleType('handleCalendarDateChange');
    // if (isCompareCalendar) {
    //   return (start: moment.Moment, end: moment.Moment, isCustom: boolean) => {
    //     if (isCustom) { handleChangeAggRangeSelected('Custom', true, true); }
    //     const clonedCompareCalendar = cloneDeep(compareCalendar);
    //     clonedCompareCalendar.displayStartDate = start;
    //     clonedCompareCalendar.displayEndDate = end;
    //     setCompareCalendar(clonedCompareCalendar);
    //   };
    // } else {
    return (start: moment.Moment, end: moment.Moment, isCustom: boolean) => {
      if (isCustom) {
        handleChangeAggRangeSelected('Custom', false, true);
      }
      const clonedCalendar = cloneDeep(calendar);
      clonedCalendar.displayStartDate = start;
      clonedCalendar.displayEndDate = end;
      setCalendar(clonedCalendar);
    };
    // }
  };

  // @ts-ignore REMOVE THIS IGNORE ONCE COMPARE CALENDAR IS NEEDED
  const handleDateRangeChange = (
    start: moment.Moment,
    end: moment.Moment,
    // isCompareCalendar: boolean,
  ): void => {
    setHandleType('handleDateRangeChange');
    const startDate = cloneDeep(start).startOf('day');
    const endDate = cloneDeep(end).endOf('day');
    // if (isCompareCalendar) {
    //   const clonedCompareCalendar = cloneDeep(compareCalendar);
    //   clonedCompareCalendar.startDate = startDate;
    //   clonedCompareCalendar.endDate = endDate;
    //   setCompareCalendar(clonedCompareCalendar);
    //   setDensityRangeAgg(getValidDateGroup(densityRangeAgg, start, end));
    // } else {
    const clonedCalendar = cloneDeep(calendar);
    clonedCalendar.startDate = startDate;
    clonedCalendar.endDate = endDate;
    setCalendar(clonedCalendar);
    setDensityRangeAgg(getValidDateGroup(densityRangeAgg, start, end));
    // }
  };

  const handleCalendarFocusedInputChange = (
    isCompareCalendar: boolean,
  ): ((focusedInput: IFocusedInput) => void) => {
    setHandleType('handleCalendarFocusedInputChange');
    if (isCompareCalendar) {
      return (focusedInput: IFocusedInput) => {
        const clonedCalendar = cloneDeep(calendar);
        // const clonedCompareCalendar = cloneDeep(compareCalendar);
        if (focusedInput === null) {
          setCalFocusedInput(null);
          clonedCalendar.displayStartDate = calendar.startDate;
          clonedCalendar.displayEndDate = calendar.endDate;
          // setCompFocusedInput(null);
          // clonedCompareCalendar.displayStartDate = compareCalendar.startDate;
          // clonedCompareCalendar.displayEndDate = compareCalendar.endDate;
        } else {
          setCalFocusedInput(null);
          // setCompFocusedInput(focusedInput);
        }
        setCalendar(clonedCalendar);
        // setCompareCalendar(clonedCompareCalendar);
      };
    } else {
      return (focusedInput: IFocusedInput) => {
        const clonedCalendar = cloneDeep(calendar);
        // const clonedCompareCalendar = cloneDeep(compareCalendar);
        if (focusedInput === null) {
          setCalFocusedInput(null);
          clonedCalendar.displayStartDate = calendar.startDate;
          clonedCalendar.displayEndDate = calendar.endDate;
          // setCompFocusedInput(null);
          // clonedCompareCalendar.displayStartDate = compareCalendar.startDate;
          // clonedCompareCalendar.displayEndDate = compareCalendar.endDate;
        } else {
          setCalFocusedInput(focusedInput);
          // setCompFocusedInput(null);
        }
        setCalendar(clonedCalendar);
        // setCompareCalendar(clonedCompareCalendar);
      };
    }
  };

  // const handleCompareEnabled = () => {
  //   setHandleType('handleCompareEnabled');
  //   setIsComparedToEnabled(!isComparedToEnabled);
  //   IASMeasurementTrackEvent('IAS Measurement Drill Dashboard - Compare Enabled');
  // };

  // filter pill functions
  const handleFilterOnChange = (menuTitle: string) => (values: IMenuList) => {
    setHandleType('handleFilterOnChange');
    const clonedFilters = cloneDeep(filters);
    clonedFilters[menuTitle] = {
      menuTitle,
      list: values,
    };
    setFilters(clonedFilters);
  };

  const handleFilterAccept = () => {
    setHandleType('handleFilterAccept');
    updateQueryParams({});
  };

  const handleClearFilters = () => {
    setHandleType('handleClearFilters');
    const clonedFilters = cloneDeep(filters);
    Object.values(clonedFilters).forEach((filter) => {
      Object.values(filter.list).forEach((item) => {
        item.selected = false;
      });
    });
    setFilters(clonedFilters);
  };

  // group by filter functions
  const handleGroupByOnChange = (_: string, value: string) => {
    setHandleType('handleGroupByOnChange');
    const newTree = getTreeFromGroupByDataKey(value, cfg.table.groupBys);
    if (newTree !== currentGroupBy.tree) {
      unstable_batchedUpdates(() => {
        setCrumbs([] as IMeasurementBreadCrumb[]);
        setFilterConfig({});
      });
    }
    const result = cfg.table.groupBys.filter((groupBy) => groupBy.dataKey === value);
    unstable_batchedUpdates(() => {
      setCurrentGroupBy(result[0]);
    });
  };

  // breadcrumb functions
  const handleBreadCrumbClick = (selectedCrumb: IMeasurementBreadCrumb) => {
    setHandleType('handleBreadCrumbClick');
    const clonedCrumbs = cloneDeep(crumbs).filter(
      (crumb) => crumb.nodeLevel < selectedCrumb.nodeLevel,
    );
    const result = cfg.table.groupBys.filter(
      (groupBy) => groupBy.label === selectedCrumb.groupByLabel,
    );
    IASMeasurementTrackEvent(user.user, {
      eventTitle: 'IAS Measurement Drill Dashboard - Click Breadcrumb',
    });
    unstable_batchedUpdates(() => {
      setCrumbs(clonedCrumbs);
      setFilterConfig(
        clonedCrumbs.length > 0
          ? {
              [clonedCrumbs[clonedCrumbs.length - 1].groupByIDKey]:
                clonedCrumbs[clonedCrumbs.length - 1].id,
            }
          : ({} as Record<string, any>),
      );
      setCurrentGroupBy(result[0]);
    });
  };

  const handleBreadCrumbClear = () => {
    setHandleType('handleBreadCrumbClear');
    unstable_batchedUpdates(() => {
      setCrumbs([] as IMeasurementBreadCrumb[]);
      setFilterConfig({});
      setCurrentGroupBy({ ...extractDefaultGroupBy(cfg.table.groupBys) });
    });
  };

  // table functions
  const handleOrderOnChange = (columnID: number) => {
    setHandleType('handleOrderOnChange');
    // we subtract 1 to get the correct column index in the config
    const colIndex = columnID === 0 ? 0 : columnID - 1;
    if (chartDataKey !== cfg.table.columns[colIndex].dataKey) {
      unstable_batchedUpdates(() => {
        setChartDataKey(cfg.table.columns[colIndex].dataKey);
        setCurrentTableOrderDirection('desc');
      });
    } else {
      setCurrentTableOrderDirection(currentTableOrderDirection === 'desc' ? 'asc' : 'desc');
    }
  };

  const handleNameLinkClickInfo = (name: string, id: string, isFromChart: boolean = false) => {
    setHandleType('handleNameLinkClickInfo');
    const filteredGroupBys = filterGroupBysByTree(currentGroupBy.tree, cfg.table.groupBys);
    if (isFromChart && currentGroupBy.nodeLevel === filteredGroupBys.length - 1) {
      return;
    }
    const updatedFilters = {} as Record<string, any>;
    updatedFilters[currentGroupBy.idKey] = id;
    if (id && id !== '0') {
      const newCrumb = {
        id,
        label: name,
        groupByLabel: currentGroupBy.label,
        groupByIDKey: currentGroupBy.idKey,
        nodeLevel: currentGroupBy.nodeLevel,
      };
      const clonedCrumbs = cloneDeep(crumbs).filter(
        (crumb) => crumb.nodeLevel < newCrumb.nodeLevel,
      );
      clonedCrumbs.push(newCrumb);
      clonedCrumbs.sort((a, b) => a.nodeLevel - b.nodeLevel);
      unstable_batchedUpdates(() => {
        if (!isCrumbUsed) {
          setIsCrumbUsed(true);
        }
        setCrumbs(clonedCrumbs);
        setFilterConfig(updatedFilters);
        setCurrentGroupBy(
          filterGroupBy('paramKey', currentGroupBy.childKey || '', cfg.table.groupBys),
        );
      });
    }
  };

  // snackbar function
  const handleSnackbarClose = () => {
    setSnackbar({
      message: '',
      error: false,
      warning: false,
    });
  };
  const handlePagination = (pageNumber: number, pageSize: number) => {
    setHandleType('handlePageChange');
    unstable_batchedUpdates(() => {
      setCurrentPage(pageNumber);
      setCurrentPageSize(pageSize);
    });
  };

  const handleTableSearch = (nameSearch: string, idSearch: string) => {
    setHandleType('handlePageChange');
    unstable_batchedUpdates(() => {
      setCurrentNameSearch(nameSearch);
      setCurrentIDSearch(idSearch);
    });
  };

  const isLoading = filtersLoadState === LoadingStateLoading;

  return (
    <Panels maxWidth vertical relative minWidthMain>
      <Panel
        largeHorizontalPadding
        verticalPadding
        style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
      >
        <ModelTopBar
          title={routeName(pathname)}
          session={session}
          user={user}
          publisher={session.publisher}
          classes={classes}
          delegateElevate={true}
          currentPath={pathname}
          disableTitlePaddingLeft
        />
      </Panel>
      <Panel scroll largeHorizontalPadding verticalPadding>
        {/* FILTERS SECTION */}
        <Grid item>
          <Paper className={classes.paper} style={{ marginTop: 8, marginBottom: 8 }}>
            <MeasurementFilters
              user={user}
              session={session}
              timezone={cfg.timezone}
              // calendar props
              range1Selected={rangeSelectedStr}
              range1DisplaySelected={rangeSelectedStr}
              calStartDate={calendar.startDate}
              calEndDate={calendar.endDate}
              calDisplayStartDate={calendar.displayStartDate}
              calDisplayEndDate={calendar.displayEndDate}
              calFocusedInput={calFocusedInput}
              // compare calendar props
              // range2Selected={compareCalendar.rangeSelected}
              // range2DisplaySelected={compareCalendar.rangeDisplaySelected}
              // compStartDate={compareCalendar.startDate}
              // compEndDate={compareCalendar.endDate}
              // compDisplayStartDate={compareCalendar.displayStartDate}
              // compDisplayEndDate={compareCalendar.displayEndDate}
              // compFocusedInput={compFocusedInput}
              // other calendar props
              // isComparedToEnabled={isComparedToEnabled}
              // handleCompareEnabled={handleCompareEnabled}
              // calendar functions
              handleChangeAggRangeSelected={handleChangeAggRangeSelected}
              handleCalendarDateChange={handleCalendarDateChange}
              handleDateRangeChange={handleDateRangeChange}
              handleCalendarFocusedInputChange={handleCalendarFocusedInputChange}
              // filters
              filters={Object.values(filters)}
              groupBys={cfg.table.groupBys}
              currentGroupBy={currentGroupBy}
              handleGroupByOnChange={handleGroupByOnChange}
              handleFilterOnChange={handleFilterOnChange}
              handleFilterAccept={handleFilterAccept}
              handleClearFilters={handleClearFilters}
            />
          </Paper>
        </Grid>
        {isLoading && (
          <Grid item>
            <LinearProgress style={{ width: '100%', height: 4 }} />
          </Grid>
        )}
        <Grid item container direction='column' style={{ marginTop: 8, marginBottom: 8 }}>
          {/* BREADCRUMB SECTION */}
          {!isEmpty(crumbs) ? (
            <Grid item style={{ paddingTop: 8, paddingLeft: 16 }}>
              <IMeasurementBreadCrumbs
                crumbs={crumbs}
                handleBreadCrumbClick={handleBreadCrumbClick}
                handleBreadCrumbClear={handleBreadCrumbClear}
              />
            </Grid>
          ) : (
            !isCrumbUsed && (
              <Grid
                item
                container
                direction='row'
                style={{
                  padding: '8px 16px',
                  backgroundColor: publicaColors.veryLightGray,
                  width: 'fit-content',
                  borderRadius: 4,
                  marginTop: 8,
                  marginLeft: 8,
                }}
              >
                <Grid style={{ marginTop: 1 }}>
                  <img src='/assets/shared/svg/LightBulb.svg' />
                </Grid>
                <Grid style={{ paddingLeft: 8 }}>
                  <Typography style={{ fontSize: 14 }}>
                    Drill into any object by clicking on the graph or table below.
                  </Typography>
                </Grid>
              </Grid>
            )
          )}
          {/* CHART SECTION */}
          {!isEmpty(queryParams) && (
            <Grid
              item
              style={{ marginTop: isEmpty(crumbs) && isCrumbUsed ? 8 : 16, marginBottom: 0 }}
            >
              <MeasurementChart
                classes={classes}
                colors={cfg.colors}
                queryParams={queryParams}
                chartInfo={cfg.chart}
                tableColumns={cfg.table.columns}
                currentGroupBy={currentGroupBy}
                user={user.user}
                setSnackbar={setSnackbar}
                currentPage={currentPage}
                currentPageSize={currentPageSize}
                currentNameSearch={currentNameSearch}
                currentIDSearch={currentIDSearch}
                currentTableOrderDirection={currentTableOrderDirection}
                handleChartItemClick={handleNameLinkClickInfo}
                chartDataKey={chartDataKey}
              />
            </Grid>
          )}
          {/* TABLE SECTION */}
          {!isEmpty(queryParams) && (
            <Grid item style={{ marginTop: 1, marginBottom: 8 }}>
              <MeasurementTable
                tableInfo={cfg.table}
                queryParams={queryParams}
                currentGroupBy={currentGroupBy}
                handleOrderOnChange={handleOrderOnChange}
                handleNameLinkClickInfo={handleNameLinkClickInfo}
                setSnackbar={setSnackbar}
                user={user.user}
                handlePagination={handlePagination}
                handleTableSearch={handleTableSearch}
                chartDataKey={chartDataKey}
              />
            </Grid>
          )}
        </Grid>
        {/* SNACKBAR */}
        <SnackbarView
          autoHideDuration={12000}
          open={!!snackbar.message}
          error={!!snackbar.error}
          onClose={handleSnackbarClose}
          messageColor={''}
          backgroundColor={''}
          closeColor={''}
          warning={!!snackbar.warning}
        >
          {snackbar.message}
        </SnackbarView>
      </Panel>
    </Panels>
  );
};

export const MeasurementDrill = decorateV2<Partial<ISessionState>>(
  cfg.defaultProps,
  _MeasurementDrill,
);
